import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Badge } from "react-bootstrap";
import {
  cancelOrder,
  fetchOrderDetails,
  generateInvoice,
} from "../../features/buyer/buyerSlice";
import Loader from "../../components/common/Loader";
import theme from "../../styles/theme/theme";
import {
  FaMapMarkerAlt,
  FaRupeeSign,
  FaTruck,
  FaDownload,
  FaDollarSign,
} from "react-icons/fa";
import { timeAgo } from "../../utils/timeAgo";
import { capitalizeFirst } from "../../utils/stringUtils";
import { formatCurrency, formatPrice } from "../../utils/price";

// ... all imports remain unchanged

const OrderDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { orderDetails, orderLoading, orderError } = useSelector(
    (state) => state.buyer
  );

  console.log("Ordr dts:", orderDetails);

  useEffect(() => {
    if (id)
      dispatch(fetchOrderDetails(id))
        .unwrap()
        .catch((err) => console.error("Failed to load order details:", err));
  }, [dispatch, id]);

  const handleGenerateInvoice = async () => {
    try {
      const res = await dispatch(generateInvoice({ order_id: id })).unwrap();

      const blobData = res?.data || res;
      const file = new Blob([blobData], { type: "application/pdf" });
      const fileURL = window.URL.createObjectURL(file);

      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `invoice_order_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => window.URL.revokeObjectURL(fileURL), 100);
    } catch (error) {
      console.error("Invoice generation failed", error);
      console.log("Failed to generate invoice. Please try again.");
    }
  };

  if (orderLoading) return <Loader />;
  if (orderError) return <div className="text-danger">{orderError}</div>;
  if (!orderDetails) return null;

  const {
    product,
    seller,
    total_price,
    shipping_cost,
    payment_status,
    payment_method,
    payment_reference,
    admin_commission,
    seller_payout,
    payment_released_at,
    tracking_number,
    status,
    created_at,
    delivered_at,
    updated_at,
    total_price_with_tax,
    unique_order_id,
    tax_amount,
  } = orderDetails;

  // Calculate total payable amount
  const totalPayableAmount =
    Number(total_price || 0) +
    Number(shipping_cost || 0) +
    Number(tax_amount || 0);

  const handleCancelOrder = () => {
    dispatch(cancelOrder(id)).unwrap();
    if (window.confirm("Are you sure you want to cancel this order?")) {
      // console.log("Cancelling order", id);
    }
  };

  return (
    <Container className="py-4 px-3 px-md-5">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <h2 style={{ color: theme.palette.text.primary, fontWeight: 600 }}>
          Order Details
        </h2>

        <button
          onClick={handleGenerateInvoice}
          style={{
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            border: "none",
            padding: "8px 14px",
            borderRadius: 8,
            fontSize: "0.9rem",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
          }}
        >
          <FaDownload size={16} />
          Invoice
        </button>
      </div>

      {/* Product Summary */}
      <div style={sectionCard}>
        <Row>
          <Col md={4}>
            <img
              src={product.image_url}
              alt={product.name}
              loading="lazy"
              style={{
                width: "100%",
                borderRadius: 12,
                objectFit: "contain",
                height: 220,
              }}
            />
          </Col>
          <Col md={8}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <h4 style={{ ...sectionTitle, margin: 0 }}>{product.name}</h4>

              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 800,
                  color: "#6c757d",
                  whiteSpace: "nowrap",
                }}
              >
                Order ID: {unique_order_id}
              </span>
            </div>
            <p style={textMuted}>
              {product.description || "No description provided."}
            </p>

            <div style={infoRow}>
              <FaMapMarkerAlt size={14} color={theme.palette.primary.main} />
              <span>{product.location}</span>
            </div>
            <div style={infoRow}>
              <strong>Unit:</strong> {product.unit_of_measurement}
            </div>
            <div style={infoRow}>
              <strong>Quantity:</strong> {product.quantity}
            </div>
            <div style={infoRow}>
              <strong>Product Price:</strong>
              {/* A$ {formatPrice(product.price)} */}
              {formatCurrency(product.price, product.currency)}
            </div>
          </Col>
        </Row>
      </div>

      {/* Order & Shipping Info */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Order & Shipping Info</h5>
        <Row>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Status:</strong>{" "}
              <Badge bg={statusColor(status)}>{capitalizeFirst(status)}</Badge>
            </div>
            <div style={infoRow}>
              <strong>Ordered:</strong> {timeAgo(created_at)}
            </div>
            <div style={infoRow}>
              <strong>Updated:</strong> {timeAgo(updated_at)}
            </div>
            <div style={infoRow}>
              <strong>Delivered At:</strong>{" "}
              {delivered_at || "Not yet delivered"}
            </div>
          </Col>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Tracking Number:</strong> {tracking_number || "N/A"}
            </div>
            {/* <div style={infoRow}>
              <FaTruck size={14} style={{ marginRight: 4 }} />
              Shipping Cost: A${shipping_cost || "-"}
            </div> */}
            {/* <div style={infoRow}>
              <strong>Total Price:</strong> A${formatPrice(total_price)}
            </div> */}
          </Col>
        </Row>
        {status === "pending" && (
          <div style={{ marginTop: 16 }}>
            <button
              onClick={handleCancelOrder}
              style={{
                padding: "10px 16px",
                backgroundColor: theme.palette.error.main,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Cancel Order
            </button>
          </div>
        )}
      </div>

      {/* Payment Info */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Payment Information</h5>
        <Row>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Status:</strong>{" "}
              <Badge bg={payment_status === "paid" ? "success" : "warning"}>
                {capitalizeFirst(payment_status)}
              </Badge>
            </div>
            <div style={infoRow}>
              <strong>Method:</strong> {payment_method || "Not specified"}
            </div>
            <div style={infoRow}>
              <strong>Reference:</strong> {payment_reference || "N/A"}
            </div>
          </Col>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Admin Commission:</strong>
              {/* A$ {formatPrice(admin_commission) || "0.00"} */}
              {formatCurrency(admin_commission)}
            </div>
            <div style={infoRow}>
              <strong>Seller Payout:</strong>
              {/* A${formatPrice(seller_payout) || "0.00"} */}
              {formatCurrency(seller_payout)}
            </div>
            <div style={infoRow}>
              <strong>Released At:</strong> {payment_released_at || "Pending"}
            </div>
          </Col>
        </Row>
      </div>

      {/* Billing Info */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Billing Summary</h5>
        <Row>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Subtotal:</strong>
              {/* A${formatPrice(total_price)} */}
              {formatCurrency(total_price)}
            </div>
            <div style={infoRow}>
              <strong>Shipping:</strong>
              {/* A${formatPrice(shipping_cost) || "0.00"} */}
              {formatCurrency(shipping_cost)}
            </div>
            <div style={infoRow}>
              <strong>Tax:</strong>
              {/* A${formatPrice(tax_amount) || "0.00"} */}
              {formatCurrency(tax_amount)}
            </div>
          </Col>
          <Col md={6}>
            <div
              style={{
                ...infoRow,
                fontSize: "1.1rem",
                fontWeight: 600,
                color: theme.palette.success.main,
              }}
            >
              Total Payable:
              {formatCurrency(
                Number(shipping_cost) === 0
                  ? total_price_with_tax
                  : totalPayableAmount
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* Seller Info */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Seller Information</h5>

        {/* Payment Pending Notice */}
        {payment_status !== "paid" && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              color: "#856404",
              padding: "10px 12px",
              borderRadius: "6px",
              marginBottom: "12px",
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          >
            <strong>Note:</strong> Seller contact details will be visible once
            the payment is completed.
          </div>
        )}

        <Row>
          {/* LEFT COLUMN */}
          <Col md={6}>
            {/* Always visible */}
            <div style={infoRow}>
              <strong>Name:</strong> {seller.name}
            </div>

            <div style={infoRow}>
              <strong>Trade Preferences:</strong>
              <div style={{ marginTop: 6 }}>
                {seller.trade_preference_names?.length ? (
                  seller.trade_preference_names.map((item, index) => (
                    <Badge
                      key={index}
                      bg="secondary"
                      style={{ marginRight: 6, marginBottom: 4 }}
                    >
                      {item}
                    </Badge>
                  ))
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>

            {/* Conditionally visible (grouped) */}
            {payment_status === "paid" && (
              <>
                <div style={infoRow}>
                  <strong>Email:</strong> {seller.email}
                </div>

                <div style={infoRow}>
                  <strong>Phone:</strong> {seller.phone}
                </div>

                <div style={infoRow}>
                  <strong>Company:</strong> {seller.company_name}
                </div>
              </>
            )}
          </Col>

          {/* RIGHT COLUMN */}
          <Col md={6}>
            {/* Always visible */}
            <div style={infoRow}>
              <strong>Address:</strong>
            </div>

            <div style={{ fontSize: "0.9rem", color: "#555", marginLeft: 8 }}>
              {!seller.addressLine1 &&
              !seller.addressLine2 &&
              !seller.city &&
              !seller.state &&
              !seller.postal_code &&
              !seller.region ? (
                "-"
              ) : (
                <>
                  {seller.addressLine1 && `${seller.addressLine1}, `}
                  {seller.addressLine2 && `${seller.addressLine2}, `}
                  {seller.city && `${seller.city}, `}
                  {seller.state && `${seller.state} - `}
                  {seller.postal_code && `${seller.postal_code}, `}
                  {seller.region}
                </>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

// Styles
const sectionCard = {
  backgroundColor: "#fff",
  padding: 24,
  borderRadius: 16,
  boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
  marginBottom: 24,
};

const sectionTitle = {
  fontWeight: 600,
  marginBottom: 16,
};

const infoRow = {
  marginBottom: 10,
  fontSize: "0.95rem",
};

const textMuted = {
  fontSize: "0.9rem",
  color: "#666",
};

const statusColor = (status) => {
  switch (status) {
    case "accepted":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "danger";
    default:
      return "secondary";
  }
};

export default OrderDetail;
